/**
 * "Acting as" in the top bar, for pages that support submission delegation.
 *
 * A page declares that it supports acting on someone's behalf by rendering
 * <ActingAs …/> (components/shared/ActingAs.tsx), which registers here while
 * the page is mounted; the top bar reads the registration and shows the
 * control. Leaving the page removes it. Each page still owns its own
 * selection — this only carries it to where it is displayed.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export interface ActingAsDelegate {
  userId: string;
  name: string;
}

export interface ActingAsSlot {
  delegates: ActingAsDelegate[];
  value: string;
  onChange: (userId: string) => void;
  /** The option meaning "for yourself", e.g. "Myself" or "My expenses". */
  selfLabel: string;
}

interface ActingAsContextValue {
  slot: ActingAsSlot | null;
  setSlot: (slot: ActingAsSlot | null) => void;
}

const ActingAsContext = createContext<ActingAsContextValue>({
  slot: null,
  setSlot: () => undefined,
});

export function ActingAsProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<ActingAsSlot | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);
  return <ActingAsContext.Provider value={value}>{children}</ActingAsContext.Provider>;
}

export function useActingAsContext(): ActingAsContextValue {
  return useContext(ActingAsContext);
}
